import { useEffect, useMemo, useState } from 'react';
import type { LostDeal, Stage } from '@epasu/core';
import { crm } from '@/lib/crm-client';
import { Button } from '@/components/ui/button';

interface Props {
  deal: LostDeal;
  /** Stufen der Pipeline, in der der Deal liegt */
  stages: Stage[];
  onClose: () => void;
  onMoved: () => void;
}

/**
 * Holt einen verlorenen Deal zurück in die Pipeline.
 *
 * Der Wechsel läuft über `moveDeal` und nicht über ein stilles Update: nur
 * dort hängen die Automations, und nur dort wird `lostAt` wieder geleert —
 * ein Deal, der wieder läuft, soll nicht weiter als „seit fünf Monaten
 * verloren“ geführt werden.
 *
 * Die Notiz bleibt bewusst stehen. Sie ist der Grund, warum der zweite Anlauf
 * überhaupt einen Ansatz hat.
 */
export function ReopenDealDialog({ deal, stages, onClose, onMoved }: Props) {
  // Verloren- und Gewonnen-Stufen sind keine Ziele: von dort ginge es direkt
  // wieder in einen Endzustand.
  const offeneStufen = useMemo(
    () => stages.filter((s) => !s.isLost && !s.isWon),
    [stages],
  );

  const [stageId, setStageId] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Zurück an den Anfang: eine erneute Ansprache fängt wieder vorn an, nicht
  // dort, wo es beim letzten Mal geendet hat.
  useEffect(() => {
    if (!stageId && offeneStufen[0]) setStageId(offeneStufen[0].id);
  }, [offeneStufen, stageId]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !busy) onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [busy, onClose]);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!stageId) return;
    setBusy(true);
    setError(null);
    try {
      await crm.pipelines.moveDeal(deal.id, stageId);
      onMoved();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Verschieben fehlgeschlagen');
      setBusy(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex justify-center overflow-y-auto bg-black/40 p-4"
      role="dialog"
      aria-modal="true"
      aria-label="Deal wieder aufnehmen"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget && !busy) onClose();
      }}
    >
      <form
        onSubmit={(e) => void submit(e)}
        className="my-auto w-full max-w-md rounded-lg border bg-background p-6 shadow-lg"
      >
        <h2 className="text-lg font-semibold">„{deal.title}“ wieder aufnehmen</h2>
        <p className="mt-1 text-sm text-muted-foreground">
          Der Deal wandert zurück in {deal.pipelineName}. Die Notiz bleibt erhalten.
        </p>

        {offeneStufen.length === 0 ? (
          <p className="mt-4 rounded-md bg-muted p-3 text-sm text-muted-foreground">
            Diese Pipeline hat keine offene Stufe, in die der Deal zurückkönnte.
          </p>
        ) : (
          <label className="mt-4 block space-y-1.5">
            <span className="text-xs font-medium text-muted-foreground">Stufe</span>
            <select
              className="h-10 w-full rounded-md border border-input bg-background px-3 text-sm"
              value={stageId}
              onChange={(e) => setStageId(e.target.value)}
            >
              {offeneStufen.map((s) => (
                <option key={s.id} value={s.id}>
                  {s.name}
                </option>
              ))}
            </select>
          </label>
        )}

        {deal.notes?.trim() && (
          <p className="mt-4 whitespace-pre-wrap rounded-md bg-muted p-3 text-sm text-muted-foreground">
            {deal.notes}
          </p>
        )}

        {error && (
          <p className="mt-4 rounded-md bg-destructive/10 p-3 text-sm text-destructive">
            {error}
          </p>
        )}

        <div className="mt-6 flex justify-end gap-2">
          <Button type="button" variant="outline" onClick={onClose} disabled={busy}>
            Abbrechen
          </Button>
          <Button type="submit" disabled={busy || offeneStufen.length === 0}>
            {busy ? 'Verschiebe …' : 'Wieder aufnehmen'}
          </Button>
        </div>
      </form>
    </div>
  );
}
